import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Layers, Plus, Edit, Trash2, Copy,
  AlertCircle, CheckCircle, XCircle, Search
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/UtopiaPages.css";

interface GlobalClass {
  id: number;
  name: string;
  tenant_id: number | null;
  createdAt: string;
}

interface School {
  id: number;
  name: string;
  subdomain: string;
  status: string;
}

const GlobalClasses: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [classes, setClasses] = useState<GlobalClass[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState<GlobalClass | null>(null);
  const [className, setClassName] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // Duplicate modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateClass, setDuplicateClass] = useState<GlobalClass | null>(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | "">("");

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchClasses();
    fetchSchools();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl("superadmin/classes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch classes");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await axios.get(getApiUrl("superadmin/schools"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchools(response.data);
    } catch (err: any) {
      console.error("Failed to fetch schools:", err);
    }
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!className.trim()) return;

    setFormLoading(true);
    try {
      if (editingClass) {
        await axios.put(
          getApiUrl(`superadmin/classes/${editingClass.id}`),
          { name: className },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Class updated successfully");
      } else {
        await axios.post(
          getApiUrl("superadmin/classes"),
          { name: className },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Class created successfully");
      }
      setShowModal(false);
      setClassName("");
      setEditingClass(null);
      fetchClasses();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save class");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(getApiUrl(`superadmin/classes/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Class deleted successfully");
      fetchClasses();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to delete class");
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateClass || !selectedSchoolId) return;

    setFormLoading(true);
    try {
      await axios.post(
        getApiUrl(`superadmin/classes/${duplicateClass.id}/duplicate`),
        { tenant_id: selectedSchoolId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Class "${duplicateClass.name}" copied to school successfully`);
      setShowDuplicateModal(false);
      setDuplicateClass(null);
      setSelectedSchoolId("");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to copy class");
    } finally {
      setFormLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    setFormLoading(true);
    try {
      const res = await axios.post(
        getApiUrl("superadmin/classes/seed-defaults"),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data?.message || "Default classes seeded");
      fetchClasses();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to seed default classes");
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (cls: GlobalClass) => {
    setEditingClass(cls);
    setClassName(cls.name);
    setShowModal(true);
  };

  const openDuplicateModal = (cls: GlobalClass) => {
    setDuplicateClass(cls);
    setSelectedSchoolId("");
    setShowDuplicateModal(true);
  };

  const filteredClasses = classes.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.2em]">Loading Classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate("/superadmin")}
              className="w-12 h-12 flex items-center justify-center bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-teal-600 hover:shadow-xl hover:shadow-teal-500/5 transition-all"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <Layers size={20} className="text-white" />
                </div>
                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em] bg-emerald-50 px-3 py-1 rounded-full">Global Templates</span>
              </div>
              <h1 className="text-3xl font-black text-slate-800 tracking-tight">Global Classes</h1>
              <p className="text-slate-400 mt-1 font-medium">Manage class templates for all schools</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSeedDefaults}
              disabled={formLoading}
              className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Load Defaults
            </button>
            <button
              onClick={() => {
                setEditingClass(null);
                setClassName("");
                setShowModal(true);
              }}
              className="flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-xl shadow-emerald-500/20"
            >
              <Plus size={16} /> Add Class
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 p-4 rounded-xl flex items-center gap-3 mb-6">
            <AlertCircle className="text-rose-500 shrink-0" size={20} />
            <p className="text-rose-700 text-sm">{error}</p>
            <button onClick={() => setError("")} className="ml-auto text-rose-500 hover:text-rose-700">
              <XCircle size={18} />
            </button>
          </div>
        )}

        {success && (
          <div className="bg-teal-50 border border-teal-200 p-4 rounded-xl flex items-center gap-3 mb-6">
            <CheckCircle className="text-teal-500 shrink-0" size={20} />
            <p className="text-teal-700 text-sm">{success}</p>
            <button onClick={() => setSuccess("")} className="ml-auto text-teal-500 hover:text-teal-700">
              <XCircle size={18} />
            </button>
          </div>
        )}

        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50 p-6 mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search classes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
            />
          </div>
        </div>

        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Class Name</th>
                <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Created</th>
                <th className="px-8 py-6 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClasses.length > 0 ? (
                filteredClasses.map((cls) => (
                  <tr key={cls.id} className="group hover:bg-slate-50/50 transition-all border-b border-slate-50">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                          <Layers size={18} className="text-emerald-600" />
                        </div>
                        <span className="font-bold text-slate-800">{cls.name}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-sm text-slate-500">
                        {cls.createdAt ? new Date(cls.createdAt).toLocaleDateString() : "-"}
                      </span>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openDuplicateModal(cls)}
                          className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-all"
                          title="Copy to school"
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          onClick={() => openEditModal(cls)}
                          className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-teal-50 hover:text-teal-600 transition-all"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(cls.id, cls.name)}
                          className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-8 py-16 text-center">
                    <Layers size={48} className="mx-auto mb-4 text-slate-200" />
                    <p className="text-slate-400 font-medium">No classes found</p>
                    <button
                      onClick={() => {
                        setEditingClass(null);
                        setClassName("");
                        setShowModal(true);
                      }}
                      className="mt-4 text-emerald-500 font-bold hover:underline"
                    >
                      Create your first class
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-black text-slate-800 mb-6">
              {editingClass ? "Edit Class" : "Create New Class"}
            </h2>
            <form onSubmit={handleCreateOrUpdate}>
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-600 mb-2 uppercase tracking-wide">
                  Class Name
                </label>
                <input
                  type="text"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  placeholder="e.g., Grade 1, Form 2"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                  autoFocus
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setClassName("");
                    setEditingClass(null);
                  }}
                  className="flex-1 px-6 py-3 border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading || !className.trim()}
                  className="flex-1 px-6 py-3 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formLoading ? "Saving..." : editingClass ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDuplicateModal && duplicateClass && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-black text-slate-800 mb-2">
              Copy to School
            </h2>
            <p className="text-slate-500 mb-6">
              Copy "{duplicateClass.name}" to a specific school
            </p>
            <div className="mb-6">
              <label className="block text-sm font-bold text-slate-600 mb-2 uppercase tracking-wide">
                Select School
              </label>
              <select
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value ? Number(e.target.value) : "")}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
              >
                <option value="">Select a school...</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name} ({school.subdomain})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateClass(null);
                  setSelectedSchoolId("");
                }}
                className="flex-1 px-6 py-3 border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDuplicate}
                disabled={formLoading || !selectedSchoolId}
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formLoading ? "Copying..." : "Copy Class"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalClasses;
